import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert, ListGroup, Pagination as BootstrapPagination } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import api from '../../services/api';

const MyViewsList = () => {
  const [viewedUsers, setViewedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    per_page: 10,
    total_pages: 1
  });

  // Fetch users I viewed
  useEffect(() => {
    const fetchViewedUsers = async () => {
      try {
        setLoading(true);
        const response = await api.get('/my-views');
        setViewedUsers(response.data.users || []);
        
        // Set pagination data if available
        if (response.data.pagination) {
          setPagination({
            page: response.data.pagination.page || 1,
            total: response.data.pagination.total || 0,
            per_page: response.data.pagination.per_page || 10,
            total_pages: response.data.pagination.total_pages || 1
          });
        }
      } catch (err) {
        console.error('Error fetching users I viewed:', err);
        setError('Failed to load profiles you viewed. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchViewedUsers();
  }, []);

  // Handle pagination
  const handlePageChange = async (page) => {
    try {
      setLoading(true);
      const response = await api.get(`/my-views?page=${page}`);
      setViewedUsers(response.data.users || []);
      
      if (response.data.pagination) {
        setPagination({
          page: response.data.pagination.page || 1,
          total: response.data.pagination.total || 0,
          per_page: response.data.pagination.per_page || 10,
          total_pages: response.data.pagination.total_pages || 1
        });
      }
    } catch (err) {
      console.error('Error fetching users I viewed:', err);
      setError('Failed to load profiles you viewed. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Render pagination controls
  const renderPagination = () => {
    if (pagination.total_pages <= 1) return null;

    return (
      <div className="d-flex justify-content-center mt-4">
        <BootstrapPagination>
          <BootstrapPagination.Prev
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page === 1}
          />
          
          {[...Array(pagination.total_pages)].map((_, index) => {
            const pageNumber = index + 1;
            // Show current page, first, last, and pages around current
            if (
              pageNumber === 1 ||
              pageNumber === pagination.total_pages ||
              (pageNumber >= pagination.page - 2 && pageNumber <= pagination.page + 2)
            ) {
              return (
                <BootstrapPagination.Item
                  key={pageNumber}
                  active={pageNumber === pagination.page}
                  onClick={() => handlePageChange(pageNumber)}
                >
                  {pageNumber}
                </BootstrapPagination.Item>
              );
            } else if (
              (pageNumber === 2 && pagination.page > 4) ||
              (pageNumber === pagination.total_pages - 1 && pagination.page < pagination.total_pages - 3)
            ) {
              return <BootstrapPagination.Ellipsis key={pageNumber} />;
            }
            return null;
          })}
          
          <BootstrapPagination.Next
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page === pagination.total_pages}
          />
        </BootstrapPagination>
      </div>
    );
  };

  if (loading && viewedUsers.length === 0) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading profiles you viewed...</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <h2 className="fw-bold mb-4">Profiles I Viewed</h2>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {viewedUsers.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-eye-slash display-4 text-muted"></i>
              <p className="mt-3 text-muted">You haven't viewed any profiles yet</p>
            </div>
          ) : (
            <ListGroup variant="flush">
              {viewedUsers.map(user => (
                <ListGroup.Item key={user.username} className="py-3 px-4">
                  <Row className="align-items-center">
                    <Col xs={12} md={8} className="d-flex align-items-center">
                      <div className="me-3">
                        {user.avatars?.small ? (
                          <img
                            src={user.avatars.small}
                            alt={user.username}
                            className="rounded-circle"
                            style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                          />
                        ) : (
                          <div 
                            className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                            style={{ width: '50px', height: '50px' }}
                          >
                            <i className="bi bi-person-fill text-secondary"></i>
                          </div>
                        )}
                      </div>
                      <div>
                        <h5 className="mb-0 fw-bold">{user.username}</h5>
                        <p className="text-muted mb-0">
                          {user.age && `${user.age} • `}
                          {user.city && user.region ? `${user.city}, ${user.region}` : (user.city || user.region || '')}
                        </p>
                      </div>
                    </Col>
                    <Col xs={12} md={4} className="mt-3 mt-md-0 text-md-end">
                      <Button
                        as={Link}
                        to={`/users/${user.username}`}
                        variant="outline-primary"
                        className="me-2"
                      >
                        View Profile
                      </Button>
                      <Button
                        as={Link}
                        to={`/messages/${user.username}`}
                        variant="primary"
                      >
                        <i className="bi bi-chat-fill me-1"></i>
                        Message
                      </Button>
                    </Col>
                  </Row>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Card.Body>
      </Card>

      {renderPagination()}
    </Container>
  );
};

export default MyViewsList;
